// Browser Supabase client.
//
// SECURITY: This client uses the public anon JWT (RLS-gated) and is safe
// to ship to the browser. The service-role key MUST NEVER be imported here
// — that lives in src/supabase/admin.ts and only runs in trusted Node
// scripts under tsx.
//
// Supabase Auth's session storage uses localStorage internally. That is
// the vendor's storage, not our app code touching localStorage. The Bible
// "no localStorage/sessionStorage" rule (§3, §24) targets app-level state;
// vendor auth persistence is the standard pattern and is permitted.

import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../db/types.ts";

type BrowserClient = SupabaseClient<Database>;

let cached: BrowserClient | null = null;

const SUPABASE_REQUEST_TIMEOUT_MS = 15_000;
const SUPABASE_FUNCTION_TIMEOUT_MS = 180_000;

function requestTimeoutMs(input: RequestInfo | URL): number {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  return url.includes("/functions/v1/")
    ? SUPABASE_FUNCTION_TIMEOUT_MS
    : SUPABASE_REQUEST_TIMEOUT_MS;
}

async function fetchWithTimeout(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), requestTimeoutMs(input));
  const upstreamSignal = init?.signal;
  const abortFromUpstream = () => controller.abort();
  upstreamSignal?.addEventListener("abort", abortFromUpstream, { once: true });

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
    upstreamSignal?.removeEventListener("abort", abortFromUpstream);
  }
}

export function getBrowserClient(): BrowserClient {
  if (cached) return cached;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      "STRIKZA: missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. " +
        "Copy .env.example to .env.local and fill in dev values.",
    );
  }

  cached = createClient<Database>(url, anonKey, {
    global: { fetch: fetchWithTimeout },
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
    },
  });
  return cached;
}
